// ProductRows.tsx
import { Box, Chip, Grid, TextField, Typography, useTheme } from '@mui/material'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { renderColorByString } from 'components/DataGrid/renderColorByString'
import { renderServiceIcon } from 'components/MetricAnalytics/renderServiceIcon'
import { useDrawer } from 'context/TaskDrawerContext/DrawerContext'
import { Dayjs } from 'dayjs'
import React, { FC, useMemo, useRef, useState } from 'react'
import { OrganizationTableItem, User } from 'types'
import { creativeTaskTypeTreeFor } from './creative_form_config'
import { taskTypeTreeFor } from './form_config'
import GlobalUserSelect from './GlobalUserSelect'
import RowActionComponent from './RowActionComponent'
import { BoxRow, denseInput, RowContainer, RowShell, TaskRadio } from './style'
import TaskOrgAutocomplete from './TaskOrgAutocomplete'
import TaskProductAutocomplete from './TaskProductAutocomplete'
import TaskTypeDatePicker from './TaskTypeDatePicker'
import { DescCtx, isChannel, isPlatform, Row, Value } from './taskTypes'
import TaskTypeTwoPane from './TaskTypeTwoPane'
import { calculateDateDue } from './taskUtil'

interface ProductRowProps {
  orgs: OrganizationTableItem[]
  rows: Row[]
  onUpdateRow: (idx: number, patch: Partial<Row>) => void
  onRemoveRow: (idx: number) => void
  productOptionsByOrg: Map<string, any[]>
  onChangeOrg: (idx: number, org: any) => void
  onClearOrg: (idx: number) => void
  onChangeProduct: (newProduct: any, row: Row, idx: number) => void
  onChangeType: (val: Value | null, row: Row, idx: number) => void
  filteredUsers: User[]
  buildTaskName: (_orgName: string, taskType: string, taskTypeLabel: string, _p?: string, _c?: string) => string
  openEditor: (ctx: DescCtx, initial?: string) => void // <- keep this name
  closeEditor: () => void
  cloneFrom: (srcIdx: number) => void
}

const ProductRows: FC<ProductRowProps> = ({
  orgs,
  rows,
  onUpdateRow,
  onRemoveRow,
  productOptionsByOrg,
  onChangeOrg,
  onClearOrg,
  onChangeProduct,
  onChangeType,
  filteredUsers,
  buildTaskName,
  // openEditor: _ignoredParentOpenEditor, // intercept
  openEditor,
  closeEditor,
  cloneFrom,
}) => {
  const theme = useTheme()
  const [hoverIdx, setHoverIdx] = useState<number | null>(null)
  const [isFocused, setIsFocused] = useState<boolean>(false)
  const { sel, open: drawerOpen, select, closeDrawer } = useDrawer()
  const hoverTimerRef = useRef<number | null>(null)
  const arm = (i: number) => {
    if (drawerOpen) return
    if (hoverTimerRef.current) window.clearTimeout(hoverTimerRef.current)
    hoverTimerRef.current = null
    setHoverIdx(i)
  }
  const disarm = (i: number) => {
    if (drawerOpen) return

    if (hoverTimerRef.current) window.clearTimeout(hoverTimerRef.current)
    hoverTimerRef.current = window.setTimeout(() => {
      setHoverIdx(prev => (prev === i ? null : prev))
    }, 140)
  }
  const updateStartDate = (idx: number, date: Dayjs, row: Row) => {
    const newDate = calculateDateDue(row?.taskType ?? null, date)
    onUpdateRow(idx, { startDate: date ?? null })
    if (newDate) onUpdateRow(idx, { dateDue: newDate ?? null })
  }

  // If selected row gets removed, close drawer

  const userById = useMemo(() => {
    const m = new Map<string, { label: string }>()
    filteredUsers.forEach(u => m.set(u.user_uuid, { label: u.name }))
    return m
  }, [filteredUsers])

  const isRowValid = (row: any) =>
    !!(row?.org?.org_uuid && row.platform && row.channel && row.taskType && row.startDate)
  const rowSx = { bgcolor: 'background.toolbar', ...denseInput }
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ width: '100%' }}>
        {rows.map((row, idx) => {
          const isPinned = sel?.kind === 'prod' && sel?.idx === idx
          const isHovered = !drawerOpen && hoverIdx === idx
          const suppressRow = drawerOpen && !isPinned
          const calcColor =
            !row?.org?.org_uuid || suppressRow ? theme.palette.text.secondary : theme.palette.text.primary

          // const twoPaneTree =
          //   isPlatform(row.platform) && isChannel(row.channel)
          //     ? taskTypeTreeFor(row.platform, row.channel, row.selectedProduct)
          //     : {}
          const twoPaneTree = row.creative?.typeKey
            ? creativeTaskTypeTreeFor(row.creative.typeKey)
            : isPlatform(row.platform) && isChannel(row.channel)
            ? taskTypeTreeFor(row.platform, row.channel, row.selectedProduct)
            : {}
          const creativeTypeValue: Value =
            row?.selectedProduct && row?.selectedProduct?.title && row?.selectedProduct?.subcategory
              ? {
                  base: 'Creative',
                  sub: row.subType ?? null,
                  label: `${row?.selectedProduct?.title} | ${row?.selectedProduct?.subcategory}`,
                }
              : { base: 'Creative', sub: row.subType ?? null, label: 'Creative Project' }
          const invalid = !isRowValid(row)

          return (
            <React.Fragment key={idx}>
              <RowShell>
                <RowContainer data-active={isHovered ? 'true' : 'false'} data-pinned={isPinned ? 'true' : 'false'}>
                  <Box sx={{ pointerEvents: 'auto', opacity: drawerOpen ? 0 : 1 }}>
                    <RowActionComponent
                      row={row}
                      idx={idx}
                      kind="prod"
                      removeRow={onRemoveRow}
                      openEditor={(ctx, initial) => openEditor(ctx, initial)}
                      cloneRowFrom={cloneFrom}
                      onHoverStart={() => arm(idx)}
                      onHoverEnd={() => disarm(idx)}
                    />
                  </Box>
                  <Grid container columns={30} spacing={1} alignItems="center" wrap="nowrap">
                    {/* Org */}
                    {/* <Grid
                      item
                      xs={6}
                      sx={t => ({
                        transition: t.transitions.create(['flex-basis', 'max-width'], {
                          duration: 200,
                          easing: t.transitions.easing.easeInOut,
                        }),
                      })}
                    > */}
                    <TaskOrgAutocomplete
                      data={orgs}
                      isLoading={false}
                      label="Organization"
                      value={row.org}
                      onChange={(org: any) => onChangeOrg(idx, org)}
                      onClear={() => onClearOrg(idx)}
                      disabled={suppressRow}
                      sx={rowSx}
                      shrinkLabel
                      placeholder="Active organizations"
                    />
                    {/* </Grid> */}
                    {/* Product (scoped by org) */}
                    {(() => {
                      const orgUuid = row?.org?.org_uuid
                      const productOptions = orgUuid ? productOptionsByOrg.get(orgUuid) ?? [] : []
                      return (
                        <TaskProductAutocomplete
                          products={productOptions}
                          value={row.selectedProduct}
                          disabled={!orgUuid || suppressRow}
                          isLoading={false}
                          label="Product"
                          error={false}
                          onChange={e => onChangeProduct(e, row, idx)}
                          sx={rowSx}
                          placeholder="Select product associated"
                        />
                      )
                    })()}
                    {/* Type (two-pane) */}
                    {/* {!row?.creative?.typeKey && ( */}
                    <TaskTypeTwoPane
                      tree={twoPaneTree}
                      disabled={row.creative?.typeKey ? true : !row.channel || suppressRow}
                      value={
                        row.creative?.typeKey
                          ? creativeTypeValue
                          : row.taskType
                          ? { base: row.taskType, sub: row.subType ?? null, label: row.taskTypeLabel || null }
                          : null
                      }
                      onChange={e => onChangeType(e, row, idx)}
                      sx={rowSx}
                      error={
                        row.creative?.typeKey ? false : row?.org?.org_uuid && row?.selectedProduct && !row?.taskType
                      }
                      isPinned={isPinned}
                      isHovered={isHovered}
                    />
                    {/* Assignees */}
                    <GlobalUserSelect
                      name={`assignees-${idx}`}
                      value={row.assignees ?? []}
                      options={filteredUsers}
                      displayLabel="Assignees"
                      onChange={ids => onUpdateRow(idx, { assignees: ids })}
                      disabled={!row?.org?.org_uuid || suppressRow}
                      sx={rowSx}
                      isPinned={isPinned}
                      isHovered={isHovered}
                      context="product"
                    />
                    {/* Start */}
                    <Grid
                      item
                      xs={isHovered ? 2.5 : 3.5}
                      sx={t => ({
                        transition: t.transitions.create(['flex-basis', 'max-width'], {
                          duration: 200,
                          easing: t.transitions.easing.easeInOut,
                        }),
                      })}
                    >
                      <TaskTypeDatePicker
                        label="Start"
                        value={row?.startDate ?? null}
                        // onChange={d => onUpdateRow(idx, { startDate: d })}
                        onChange={d => updateStartDate(idx, d, row)}
                        disabled={!row?.org?.org_uuid || suppressRow}
                        slotProps={{
                          textField: {
                            size: 'small',
                            sx: rowSx,
                            error: !row.startDate && (row.platform || row.channel || row.taskType) ? true : false,
                            inputProps: {
                              sx: { color: calcColor, WebkitTextFillColor: calcColor },
                            },
                            InputLabelProps: {
                              shrink: true,
                              sx: { color: calcColor, WebkitTextFillColor: calcColor },
                            },
                            placeholder: 'Select start date',
                          },
                        }}
                      />
                    </Grid>
                    {/* Due */}
                    <Grid
                      item
                      xs={isHovered ? 2.5 : 3.5}
                      sx={t => ({
                        transition: t.transitions.create(['flex-basis', 'max-width'], {
                          duration: 200,
                          easing: t.transitions.easing.easeInOut,
                        }),
                      })}
                    >
                      <TaskTypeDatePicker
                        label="Due"
                        value={row?.dateDue ?? null}
                        onChange={d => onUpdateRow(idx, { dateDue: d })}
                        disabled={!row?.org?.org_uuid || suppressRow}
                        slotProps={{
                          textField: {
                            size: 'small',
                            sx: rowSx,
                            error: !row.startDate && (row.platform || row.channel || row.taskType) ? true : false,
                            inputProps: {
                              sx: { color: calcColor, WebkitTextFillColor: calcColor },
                              readOnly: true,
                            },
                            InputLabelProps: {
                              shrink: true,
                              sx: { color: calcColor, WebkitTextFillColor: calcColor },
                            },
                            placeholder: 'Select date due',
                          },
                        }}
                      />
                    </Grid>
                    {/* Details */}
                    <Grid
                      item
                      xs={isFocused ? 12 : isHovered ? 2.5 : 3.5}
                      sx={t => ({
                        transition: t.transitions.create(['flex-basis', 'max-width'], {
                          duration: 200,
                          easing: t.transitions.easing.easeInOut,
                        }),
                      })}
                    >
                      <TextField
                        label="Details"
                        size="small"
                        value={row?.details ?? ''}
                        onFocus={() => setIsFocused(true)}
                        onBlur={() => setIsFocused(false)}
                        onChange={e => onUpdateRow(idx, { details: e.target.value })}
                        disabled={!row?.org?.org_uuid || suppressRow}
                        placeholder="Short summary (e.g., Q1 campaign scaling)"
                        InputLabelProps={{ shrink: true }}
                        sx={{
                          ...rowSx,
                          '& .MuiInputBase-input::placeholder': {
                            opacity: 0.6,
                            fontStyle: 'italic',
                          },
                        }}
                        inputProps={{
                          sx: { color: calcColor, WebkitTextFillColor: calcColor },
                        }}
                      />
                    </Grid>
                  </Grid>

                  {/* Inline helpers */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    {row?.org?.name && row?.taskType && (
                      <BoxRow data-suppress={suppressRow}>
                        <Typography variant="caption">Name:</Typography>
                        <Typography
                          variant="caption"
                          fontWeight="bold"
                          color={invalid ? 'text.disabled' : 'text.highlight'}
                          noWrap
                        >
                          {buildTaskName(row?.org?.name, row.taskType, row.taskTypeLabel)}
                        </Typography>
                      </BoxRow>
                    )}
                    {row?.platform && (
                      <BoxRow data-suppress={suppressRow}>
                        <Typography variant="caption">Platform:</Typography>
                        {row?.platform && row?.selectedProduct?.title && row?.selectedProduct?.instanceId ? (
                          <img
                            key={`${row?.selectedProduct?.title}-${row?.selectedProduct?.instanceId}`}
                            src={renderServiceIcon(row?.selectedProduct?.title)}
                            alt={row?.selectedProduct?.title}
                            style={{ width: 14, height: 14 }}
                          />
                        ) : (
                          <Typography
                            variant="caption"
                            fontWeight="bold"
                            color={invalid ? 'text.disabled' : 'text.highlight'}
                            noWrap
                          >
                            {row?.platform}
                          </Typography>
                        )}
                      </BoxRow>
                    )}
                    {row?.channel && (
                      <BoxRow data-suppress={suppressRow}>
                        <Typography variant="caption">Channel:</Typography>
                        <Typography
                          variant="caption"
                          fontWeight="bold"
                          sx={{ color: renderColorByString(row?.channel) }}
                          noWrap
                        >
                          {row?.channel}
                        </Typography>
                      </BoxRow>
                    )}
                    {!!row.assignees?.length && (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {row.assignees.map(id => (
                          <Chip key={id} size="small" label={userById.get(id)?.label ?? id} />
                        ))}
                      </Box>
                    )}
                  </Box>

                  {!!row.checklist?.length && (
                    <Box
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 0.75,
                        filter: suppressRow ? 'grayscale(100%)' : '',
                      }}
                    >
                      {row.checklist.map(item => (
                        <Chip key={item.id} size="small" label={item.label} variant="outlined" />
                      ))}
                    </Box>
                  )}
                </RowContainer>
                <TaskRadio
                  data-task-radio
                  size="small"
                  onMouseEnter={() => arm(idx)}
                  onMouseLeave={() => disarm(idx)}
                  sx={{ pointerEvents: 'auto', cursor: 'pointer' }}
                  onMouseDown={e => {
                    e.stopPropagation()
                  }}
                  onClick={e => {
                    e.stopPropagation()
                    select('prod', idx)
                    setHoverIdx(null)
                  }}
                  checked={isPinned}
                />
              </RowShell>
            </React.Fragment>
          )
        })}
      </Box>
    </LocalizationProvider>
  )
}

export default ProductRows
